import React, { useState, useEffect, useRef } from 'react';
import { FiColumns } from 'react-icons/fi';
import { motion } from 'framer-motion';
import Button from '../components/Button';

export default function StaggeredColumnSelector({ onApplyColumns }) {
  const dropdownRef = useRef(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const allColumns = [
    "Código", "Título", "Fase", "Responsável", "Desenquadramento Mei", "Empresa",
    "Etapas do Processo", "Qual a razão social/nome empresarial desejada?",
    "Categoria (Para cobrança da abertura)", "Previsão de fechamento",
    "Data do fechamento esperado", "Comentários", "Processo em cartório",
    "Segmento", "Contrato assinado:", "Temperatura do Negócio", "Email",
    "Plano de contas", "Qual nome fantasia desejado?", "Contrato registrado",
    "Informação sobre alteração contratual",
    "Qual telefone deve ser adicionado no cartão CNPJ da empresa?",
    "Observações:", "Documentos:", "A empresa tem movimento?",
    "Nome do Cliente", "CNPJ", "A empresa tem quantos funcionários?",
    "Enviar contrato ao cliente",
    "Qual e-mail deve ser adicionado no cartão CNPJ da empresa?",
    "Razão Social", "Qual capital social da empresa?", "CPF do Cliente",
    "Telefone", "Honorário mensal do contrato por extenso", "Nome Fantasia",
    "A empresa precisa que um dos sócios seja o \"responsável legal\". Qual o escolhido?",
    "Honorário de Abertura", "Defina quem será o sócio administrador?",
    "Tipo de Entrada", "Regime", "Proposta enviada para o cliente",
    "Valor (R$) Honorário de abertura", "Tipo de processo",
    "Descreva as atividades que serão exercidas pela empresa",
    "Valor (R$) Honorário mensal", "Endereço do Estabelecimento",
    "Checagem de segurança", "Observações sobre o Lead",
    "Documento de identidade dos sócios (CNH ou RG, contendo CPF)",
    "Comprovante de residência dos sócios", "A empresa possui quantos funcionários?",
    "Comentários:", "Contatos", "Empresa tem movimento?",
    "Número da inscrição imobiliária (pode ser encontrado no extrato do IPTU)",
    "Ramo de atuação", "Título de eleitor do responsável ou Recibo de IRPF",
    "Responsável pela venda", "Confirmo que enviei as informações",
    "Canal de vendas", "Preenchido", "Indicado por", "Participação dos sócios",
    "Unidade", "Competência Inicial", "Data de hoje para Contrato",
    "Data do primeiro vencimento", "Observações sobre o lead",
    "Endereço", "No", "Bairro", "Complemento", "Cidade", "Estado", "UF",
    "CEP", "Atividade econômica", "Capital Social", "Situação Cadastral",
    "Inscrição Estadual", "Procuração", "Categoria",
    "Primeira vez que entrou na fase Contrato Assinado",
    "Primeira vez que entrou na fase Lead quente",
    "Primeira vez que entrou na fase Negociação",
    "Primeira vez que entrou na fase Abertura vendida",
    "Primeira vez que entrou na fase Ganho (falta contrato)",
    "Primeira vez que entrou na fase Não Convertido",
    "Primeira vez que entrou na fase Arquivo",
    "Primeira vez que entrou na fase Perdido"
  ];

  const defaultColumns = ["Código", "Título", "Fase", "Responsável"];

  const columnFieldMap = {};
  allColumns.forEach(col => {
    const field = col
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '_')
      .replace(/_+/g, '_')
      .replace(/^_|_$/g, '');
    columnFieldMap[col] = field;
  });

  const [selectedColumns, setSelectedColumns] = useState(() => {
    const initialState = {};
    allColumns.forEach(col => {
      initialState[col] = defaultColumns.includes(col);
    });
    return initialState;
  });

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isDropdownOpen && dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isDropdownOpen]);

  const toggleColumn = (event, column) => {
    event.stopPropagation();
    setSelectedColumns(prev => ({
      ...prev,
      [column]: !prev[column]
    }));
  };

  const selectAllColumns = (event) => {
    event.stopPropagation();
    const newState = {};
    allColumns.forEach(col => {
      newState[col] = true;
    });
    setSelectedColumns(newState);
  };

  const deselectAllColumns = (event) => {
    event.stopPropagation();
    const newState = {};
    allColumns.forEach(col => {
      newState[col] = false;
    });
    setSelectedColumns(newState);
  };

  const applySelectedColumns = () => {
    if (onApplyColumns) {
      onApplyColumns(selectedColumns, columnFieldMap);
    }
    setIsDropdownOpen(false);
  };

  return (
    <div ref={dropdownRef} className="relative">
      <motion.div animate={isDropdownOpen ? "open" : "closed"} className="relative">
        <Button variant="secondary" onClick={() => setIsDropdownOpen(prev => !prev)} className="p-2">
          <FiColumns size={18} />
        </Button>
        {isDropdownOpen && (
          <motion.ul
            initial={{ scaleY: 0 }}
            animate={{ scaleY: 1 }}
            exit={{ scaleY: 0 }}
            style={{ originY: 'top', translateX: '-50%' }}
            className="flex flex-col gap-2 absolute top-[120%] left-[50%] w-80 bg-white rounded-lg shadow-xl z-50 overflow-hidden"
          >
            <div className="p-3 border-b flex justify-between">
              <button onClick={selectAllColumns} className="text-sm text-[#3f1057] hover:underline">
                Selecionar Todos
              </button>
              <button onClick={deselectAllColumns} className="text-sm text-[#3f1057] hover:underline">
                Desmarcar Todos
              </button>
            </div>

            <div className="max-h-80 overflow-y-auto p-2">
              {allColumns.map((column, index) => (
                <motion.div
                  key={index}
                  className="flex items-center w-full p-2 text-xs font-medium rounded-md hover:bg-gray-100 text-slate-700 transition-colors cursor-pointer"
                  onClick={(e) => toggleColumn(e, column)}
                >
                  <div className="mr-2">
                    <div className={`w-5 h-5 rounded flex items-center justify-center border ${selectedColumns[column] ? 'bg-[#3f1057] border-[#3f1057]' : 'border-gray-400'}`}>
                      {selectedColumns[column] && <FiColumns className="text-white" size={12} />}
                    </div>
                  </div>
                  <span className="truncate">{column}</span>
                </motion.div>
              ))}
            </div>

            <div className="p-3 border-t bg-gray-50 flex justify-center">
              <button
                onClick={applySelectedColumns}
                className="bg-[#3f1057] text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-[#4d1369] transition-colors w-full"
              >
                Aplicar Colunas
              </button>
            </div>
          </motion.ul>
        )}
      </motion.div>
    </div>
  );
}
